/** The subject being read, rather than the application surrounding it. */
export type PageContent = { title?: string; text: string };

const excluded =
  'script, style, noscript, template, svg, nav, aside, [role="navigation"], [role="banner"], [role="toolbar"], [hidden], [aria-hidden="true"], [data-tldr-ignore], [data-tldr-reading]';
const blocks =
  /^(ADDRESS|ARTICLE|BLOCKQUOTE|DIV|DL|DT|DD|FIGCAPTION|FIGURE|FOOTER|H[1-6]|HEADER|LI|MAIN|OL|P|PRE|SECTION|TABLE|TR|UL)$/;

export function pageContent(
  doc: Document | undefined = typeof document === "undefined" ? undefined : document,
  max = 6000,
): PageContent {
  if (!doc) return { text: "" };
  // A refresh must read the original page while its summary projection is displayed.
  const condensed = Array.from(doc.querySelectorAll(".docit-tldr-reading"));
  for (const element of condensed) element.classList.remove("docit-tldr-reading");
  try {
    const visibility = new Map<Element, boolean>();
    const displays = new Map<Element, string>();
    const readable = (element: Element): boolean => {
      const cached = visibility.get(element);
      if (cached !== undefined) return cached;
      const style = doc.defaultView?.getComputedStyle(element);
      displays.set(element, style?.display ?? "");
      const visible =
        !element.matches(excluded) &&
        style?.display !== "none" &&
        style?.visibility !== "hidden" &&
        style?.visibility !== "collapse" &&
        !(element.tagName === "DETAILS" && !element.hasAttribute("open")) &&
        (!element.parentElement || readable(element.parentElement));
      visibility.set(element, visible);
      return visible;
    };
    const candidates = (selector: string) =>
      Array.from(doc.querySelectorAll<HTMLElement>(selector)).filter(readable);
    const outermost = (elements: HTMLElement[]) =>
      elements.filter(
        (element) => !elements.some((other) => other !== element && other.contains(element)),
      );

    // Page owners can name the exact reading surface. The fallback works for pages
    // that have not adopted the contract, without ever falling back to the app shell.
    let roots = outermost(candidates("[data-tldr-content]"));
    if (!roots.length) roots = outermost(candidates("main article, main .ProseMirror"));
    if (!roots.length) {
      const mains = candidates("main");
      roots = mains.filter(
        (element) => !mains.some((other) => other !== element && element.contains(other)),
      );
    }
    if (!roots.length) roots = outermost(candidates("article"));

    const read = (node: Node): string => {
      if (node.nodeType === 3) return (node.textContent ?? "").replace(/\s+/g, " ");
      if (node.nodeType !== 1) return "";
      const element = node as HTMLElement;
      if (!readable(element) || element.tagName === "SUMMARY") return "";
      if (element.tagName === "BR") return "\n";
      if (element.tagName === "INPUT") {
        const input = element as HTMLInputElement;
        if (!["checkbox", "radio"].includes(input.type)) return "";
        const name = (input.getAttribute("aria-label") ?? input.labels?.[0]?.textContent ?? "")
          .replace(/\s+/g, " ")
          .trim()
          .slice(0, 160);
        return name
          ? `\n${name}: ${input.indeterminate ? "mixed" : input.checked ? "ON (checked)" : "OFF (unchecked)"}\n`
          : "";
      }
      if (element.matches("textarea, select")) return "";
      const text = Array.from(element.childNodes, read).join("");
      if (element.tagName === "TD" || element.tagName === "TH") return `${text}\t`;
      const block =
        blocks.test(element.tagName) ||
        ["block", "flex", "grid", "list-item", "table-row"].includes(displays.get(element) ?? "");
      return block ? `\n${text}\n` : text;
    };
    const heading = roots
      .flatMap((root) => Array.from(root.querySelectorAll("h1, h2, h3")))
      .find(readable);
    const title = (
      roots[0]?.getAttribute("data-tldr-title") ||
      (heading && read(heading)) ||
      roots[0]?.getAttribute("aria-label")
    )
      ?.trim()
      .slice(0, 200);
    const text = roots
      .map(read)
      .join("\n\n")
      .replace(/[ \t]+\n/g, "\n")
      .replace(/\n[ \t]+/g, "\n")
      .replace(/\n{3,}/g, "\n\n")
      .trim();
    return { title, text: text.length > max ? `${text.slice(0, max)}…` : text };
  } finally {
    for (const element of condensed) element.classList.add("docit-tldr-reading");
  }
}
